import React, { useState } from "react";

import SimplePropertiesPanel from "./SimplePropertiesPanel";
import AdvancedPropertiesPanel from "./AdvancedPropertiesPanel";
import DeveloperPropertiesPanel from "./DeveloperPropertiesPanel";
import AiDesignPanel from "./AiDesignPanel";
import RoomEditorPanel from "../room/RoomEditorPanel";

const MODES = [
  {
    key: "simple",
    label: "Design",
    description: "Common design controls",
  },
  {
    key: "room",
    label: "Room",
    description: "Walls, openings and services",
  },
  {
    key: "advanced",
    label: "Advanced",
    description: "Precise element editing",
  },
  {
    key: "developer",
    label: "Developer",
    description: "Parameters and debug",
  },
  {
    key: "ai",
    label: "AI",
    description: "Generate and assist",
  },
];

function getNodeLabel(node) {
  return node?.name || node?.definition?.name || node?.id || "No selection";
}

function getNodeMeta(node) {
  return node?.category || node?.definition?.category || node?.nodeType || "";
}

function ModeButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition",
        active
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export default function EditorRightPanel(props) {
  const [mode, setMode] = useState("simple");

  const actualNode = props.resolvedNode;
  const simpleNode = props.simpleResolvedNode || props.resolvedNode;

  const isSimpleParentEditing =
    mode === "simple" &&
    actualNode?.id &&
    simpleNode?.id &&
    String(actualNode.id) !== String(simpleNode.id);

  const activeHeaderNode =
  mode === "room" ? { name: "Room setup", category: "walls / openings / services" } :
  isSimpleParentEditing ? simpleNode :
  actualNode;

  const activeMode = MODES.find((item) => item.key === mode) || MODES[0];

  return (
    <div className="h-full min-h-0 flex flex-col bg-white">
      <div className="shrink-0 border-b bg-white">
        <div className="px-3 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Properties
              </div>

              <div className="font-semibold text-sm text-slate-800 truncate">
                {getNodeLabel(activeHeaderNode)}
              </div>

              <div className="text-xs text-slate-400 truncate">
                {getNodeMeta(activeHeaderNode) || activeMode.description}
              </div>

              {isSimpleParentEditing && (
                <div className="mt-2 rounded border border-blue-100 bg-blue-50 px-2 py-1 text-[11px] text-blue-700">
                  Selected:{" "}
                  <span className="font-medium">
                    {getNodeLabel(actualNode)}
                  </span>{" "}
                  · editing parent cabinet
                </div>
              )}
            </div>

            <div className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-500">
              {activeMode.label}
            </div>
          </div>
        </div>

        <div className="px-2 flex gap-1 overflow-x-auto border-t bg-slate-50">
          {MODES.map((item) => (
            <ModeButton
              key={item.key}
              active={mode === item.key}
              onClick={() => setMode(item.key)}
            >
              {item.label}
            </ModeButton>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-3 bg-slate-50">
        {mode === "simple" && <SimplePropertiesPanel {...props} />}
        {mode === "advanced" && <AdvancedPropertiesPanel {...props} />}
        {mode === "developer" && <DeveloperPropertiesPanel {...props} />}
        {mode === "ai" && <AiDesignPanel {...props} />}
        {mode === "room" && <RoomEditorPanel {...props} />}
      </div>
    </div>
  );
}